import React, { useState, useContext, useEffect } from "react";
import { FirebaseContext } from "../../../context/Firebase";
import { DataContext } from "../../../context/Data";
import Toast from "../../../components/Toast/Toast";
import { isMultiInstanceEnabled } from "../../../config/multiInstance";
import {
  AiOutlineEye,
  AiOutlineEyeInvisible,
  AiOutlineLoading3Quarters,
  AiOutlinePlus,
  AiOutlineDelete,
  AiOutlineDown,
  AiOutlineRight,
} from "react-icons/ai";

const BASE = "cam";
const DEFAULT_ENTRY = { enabled: false, apiKey: "", clientId: "", description: "" };

const displayName = (key) => (key === BASE ? "Campaign Monitor" : `Campaign Monitor ${key.split("_")[1]}`);

const CampaignMonitor = ({ credentialsData, onCredentialsSaved }) => {
  const { functions } = useContext(FirebaseContext);
  const { account } = useContext(DataContext);
  const [entries, setEntries] = useState([{ key: BASE, ...DEFAULT_ENTRY }]);
  const [showApiKeys, setShowApiKeys] = useState({});
  const [openAccordions, setOpenAccordions] = useState({ [BASE]: true });
  const [testingKey, setTestingKey] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [togglingKey, setTogglingKey] = useState(null);
  const [deletingKey, setDeletingKey] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { key }
  const [toast, setToast] = useState(null);

  // Load existing instances from credentialsData (flat object: cam, cam_1, cam_2, ...)
  useEffect(() => {
    if (!credentialsData) return;

    const keys = Object.keys(credentialsData)
      .filter((k) => k === BASE || new RegExp(`^${BASE}_\\d+$`).test(k))
      .sort((a, b) => {
        const numA = a === BASE ? 0 : parseInt(a.split("_")[1], 10);
        const numB = b === BASE ? 0 : parseInt(b.split("_")[1], 10);
        return numA - numB;
      });

    if (keys.length === 0) return;

    const accordions = {};
    const loaded = keys.map((key) => {
      accordions[key] = true;
      const data = credentialsData[key] || {};
      return {
        key,
        enabled: data.enabled ?? false,
        apiKey: "",
        clientId: data.client_id || "",
        description: data.description || "",
      };
    });

    setEntries(loaded);
    setOpenAccordions(accordions);
  }, [credentialsData]);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const getNextKey = () => {
    const nums = entries.map((e) => (e.key === BASE ? 0 : parseInt(e.key.split("_")[1], 10)));
    return `${BASE}_${Math.max(0, ...nums) + 1}`;
  };

  const handleAddEntry = () => {
    const newKey = getNextKey();
    setEntries((prev) => [...prev, { key: newKey, ...DEFAULT_ENTRY }]);
    setOpenAccordions((prev) => ({ ...prev, [newKey]: true }));
  };

  const handleEntryChange = (index, field, value) => {
    setEntries((prev) => prev.map((entry, i) => (i === index ? { ...entry, [field]: value } : entry)));
  };

  const toggleAccordion = (key) => {
    setOpenAccordions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDeleteEntry = async () => {
    if (!confirmDelete) return;
    const { key } = confirmDelete;
    const existsInFirebase = !!credentialsData?.[key];

    if (existsInFirebase) {
      setDeletingKey(key);
      try {
        const result = await functions.onboarding("account/delete/credential", { account_eid: account, api: key });

        if (result.data.success) {
          showToast(`${displayName(key)} removed successfully!`, "success");
          // onCredentialsSaved() reloads credentialsData, which re-derives `entries` from
          // scratch (see the effect above) — filtering by a STALE index captured when the
          // modal opened would drop whatever row now sits at that position instead of `key`.
          if (onCredentialsSaved) await onCredentialsSaved();
        } else {
          showToast(result.data.message || "Failed to remove", "error");
          setConfirmDelete(null);
          setDeletingKey(null);
          return;
        }
      } catch (error) {
        console.error("Delete error:", error);
        showToast(error.message || "Failed to remove", "error");
        setConfirmDelete(null);
        setDeletingKey(null);
        return;
      } finally {
        setDeletingKey(null);
      }
    }

    setEntries((prev) => prev.filter((entry) => entry.key !== key));
    setOpenAccordions((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setConfirmDelete(null);
  };

  const handleTestConnection = async (index) => {
    const entry = entries[index];
    // credentialsData[entry.key].password comes back MASKED (see maskPassword in campaigns.js)
    // — it must never be sent to testConnection, or the call always fails against the real API.
    // A real key has to be typed in; there is no "test the already-saved key" path.
    const apiKey = entry.apiKey.trim() && !entry.apiKey.includes("*") ? entry.apiKey : null;

    if (!apiKey) {
      showToast("Please enter the API Key", "error");
      return;
    }

    setTestingKey(entry.key);
    try {
      // testConnection's dispatch table is keyed by base vendor type, never the instance key.
      const result = await functions.testConnection({ account_eid: account, api: BASE, user_name: "eno", password: apiKey });

      if (result.data.result) {
        showToast("Connection successful!", "success");
      } else {
        showToast(result.data.message || "Connection failed", "error");
      }
    } catch (error) {
      console.error("Test connection error:", error);
      showToast(error.message || "Failed to test connection", "error");
    } finally {
      setTestingKey(null);
    }
  };

  const handleToggleEnabled = async (index) => {
    const entry = entries[index];
    const newStatus = !entry.enabled;

    if (newStatus === false) {
      setTogglingKey(entry.key);
      try {
        const result = await functions.onboarding("account/toggle/campaigns", {
          account_eid: account,
          api: entry.key,
          enabled: false,
        });

        if (result.data.success) {
          handleEntryChange(index, "enabled", false);
          showToast("Integration disabled successfully!", "success");
          if (onCredentialsSaved) await onCredentialsSaved();
        } else {
          showToast(result.data.message || "Failed to disable integration", "error");
        }
      } catch (error) {
        console.error("Toggle error:", error);
        showToast(error.message || "Failed to disable integration", "error");
      } finally {
        setTogglingKey(null);
      }
    } else {
      handleEntryChange(index, "enabled", true);
    }
  };

  const handleSaveCredentials = async (index) => {
    const entry = entries[index];
    const hasNewApiKey = entry.apiKey.trim() && !entry.apiKey.includes("*");

    if (!hasNewApiKey && !credentialsData?.[entry.key]?.password) {
      showToast("Please enter the API Key", "error");
      return;
    }

    setSavingKey(entry.key);
    try {
      const credentials = {
        account_eid: account,
        api: entry.key,
        client_id: entry.clientId,
        description: entry.description,
        enabled: entry.enabled,
      };

      if (hasNewApiKey) {
        credentials.user_name = "eno";
        credentials.password = entry.apiKey;
      }

      const result = await functions.onboarding("account/save/credentials", credentials);

      if (result.data.success) {
        showToast("Credentials saved successfully!", "success");
        if (onCredentialsSaved) await onCredentialsSaved();
      } else {
        showToast(result.data.message || "Failed to save credentials", "error");
      }
    } catch (error) {
      console.error("Save credentials error:", error);
      showToast(error.message || "Failed to save credentials", "error");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="max-w-2xl">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="space-y-4">
        {entries.map((entry, index) => {
          const isTesting = testingKey === entry.key;
          const isSaving = savingKey === entry.key;
          const isToggling = togglingKey === entry.key;
          const label = displayName(entry.key);

          return (
            <div key={entry.key} className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <button
                type="button"
                onClick={() => toggleAccordion(entry.key)}
                className="w-full flex items-center justify-between p-6 text-left"
              >
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-semibold text-gray-900">{label}</h2>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      entry.enabled ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {entry.enabled ? "Active" : "Disabled"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {entries.length > 1 && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDelete({ key: entry.key });
                      }}
                      className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                      title="Remove entry"
                    >
                      <AiOutlineDelete className="h-5 w-5" />
                    </span>
                  )}
                  {openAccordions[entry.key] ? (
                    <AiOutlineDown className="h-5 w-5 text-gray-500" />
                  ) : (
                    <AiOutlineRight className="h-5 w-5 text-gray-500" />
                  )}
                </div>
              </button>

              {openAccordions[entry.key] && (
                <div className="px-6 pb-6 border-t border-gray-100">
                  <div className="mt-4 mb-6 flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Integration Status</label>
                      <p className="text-xs text-gray-500 mt-1">
                        {isToggling ? "Processing..." : entry.enabled ? "Integration is active" : "Integration is disabled"}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {isToggling && <AiOutlineLoading3Quarters className="h-5 w-5 text-blue-600 animate-spin" />}
                      <button
                        type="button"
                        role="switch"
                        aria-checked={entry.enabled}
                        disabled={isToggling}
                        onClick={() => handleToggleEnabled(index)}
                        className={`${
                          isToggling ? "bg-gray-400 cursor-not-allowed" : entry.enabled ? "bg-blue-600" : "bg-gray-300"
                        } relative inline-flex h-7 w-12 items-center flex-shrink-0 rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          isToggling ? "opacity-70" : "cursor-pointer"
                        }`}
                      >
                        <span
                          className={`${
                            entry.enabled ? "translate-x-6" : "translate-x-1"
                          } inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition duration-200 ease-in-out`}
                        />
                      </button>
                    </div>
                  </div>

                  {entry.enabled && (
                    <>
                      <div className="mb-4 flex flex-col sm:flex-row sm:items-end gap-3">
                        <div className="flex-[2] min-w-[220px]">
                          <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                          <div className="relative">
                            <input
                              type={showApiKeys[entry.key] ? "text" : "password"}
                              value={entry.apiKey}
                              onChange={(e) => handleEntryChange(index, "apiKey", e.target.value)}
                              placeholder={credentialsData?.[entry.key]?.password || "Campaign Monitor API key"}
                              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <button
                              type="button"
                              onClick={() => setShowApiKeys((prev) => ({ ...prev, [entry.key]: !prev[entry.key] }))}
                              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
                            >
                              {showApiKeys[entry.key] ? (
                                <AiOutlineEyeInvisible className="h-5 w-5" />
                              ) : (
                                <AiOutlineEye className="h-5 w-5" />
                              )}
                            </button>
                          </div>
                        </div>

                        <div className="flex-1 min-w-[140px]">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Client ID <span className="text-gray-400 font-normal">(optional)</span>
                          </label>
                          <input
                            type="text"
                            value={entry.clientId}
                            onChange={(e) => handleEntryChange(index, "clientId", e.target.value)}
                            placeholder="First client"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>

                        <div className="flex-1 min-w-[140px]">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description <span className="text-gray-400 font-normal">(optional)</span>
                          </label>
                          <input
                            type="text"
                            value={entry.description}
                            onChange={(e) => handleEntryChange(index, "description", e.target.value)}
                            placeholder="Description"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>
                      <p className="mt-[-8px] mb-4 text-xs text-gray-500">
                        {credentialsData?.[entry.key]?.password
                          ? "Current API key shown as placeholder. Leave empty to keep current."
                          : "Find your API key and Client ID in Campaign Monitor Account Settings."}
                      </p>

                      <div className="flex space-x-3">
                        <button
                          onClick={() => handleTestConnection(index)}
                          disabled={isTesting || isSaving}
                          className={`px-4 py-2 text-sm font-medium rounded-md border border-gray-300 ${
                            isTesting || isSaving ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          {isTesting ? "Testing..." : "Test Connection"}
                        </button>
                        <button
                          onClick={() => handleSaveCredentials(index)}
                          disabled={isSaving || isTesting || (!entry.apiKey.trim() && !credentialsData?.[entry.key]?.password)}
                          className={`px-4 py-2 text-sm font-medium rounded-md ${
                            isSaving || isTesting || (!entry.apiKey.trim() && !credentialsData?.[entry.key]?.password)
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-gray-800 hover:bg-gray-700"
                          } text-white flex items-center gap-2`}
                        >
                          {isSaving && <AiOutlineLoading3Quarters className="h-4 w-4 animate-spin" />}
                          {isSaving ? "Saving..." : "Save Credentials"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {isMultiInstanceEnabled(BASE) && (
        <button
          type="button"
          onClick={handleAddEntry}
          className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
        >
          <AiOutlinePlus className="h-5 w-5" />
          Add Campaign Monitor Account
        </button>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => !deletingKey && setConfirmDelete(null)} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Deletion</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to remove <strong>{displayName(confirmDelete.key)}</strong>? This will permanently delete the
              credentials and configuration from Firebase.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setConfirmDelete(null)}
                disabled={!!deletingKey}
                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteEntry}
                disabled={!!deletingKey}
                className="px-4 py-2 text-sm font-medium rounded-md bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 flex items-center gap-2"
              >
                {deletingKey && <AiOutlineLoading3Quarters className="h-4 w-4 animate-spin" />}
                {deletingKey ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignMonitor;
